import { IMAGE_PATHS } from 'constants/imagePaths';
import { useFormik } from 'formik';
import useTranslate from 'hooks/useTranslate';
import { ReactNode, useMemo, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import ReactSelect from 'react-select';
import { calculateTimeSpan } from 'utils';
import * as Yup from 'yup';
import ActionMenu from '../ActionMenu';
import AddMoreButton from '../AddMoreButton';
import CityList from '../CommonComponents/CityList';
import StateList from '../CommonComponents/StateList';
import InfoCard from '../InfoCard';
import { useCountryListQuery } from 'services';
import useResumeStore from 'store/resume';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';
import { minDate } from 'constants';

interface EducationInfoInterface {
	degree: string;
	instituteName: string;
	instituteLogo: string;
	startDate: string;
	endDate: string;
	countryId: string;
	stateId: string;
	cityId: string;
	currentlyStudying: boolean;
	address: string;
}

const EducationInfo = ({
	data,
	slug,
	handleAddMoreClick,
	handleRemoveClick,
	onHandleChange,
	handleFileChange,
	handleModalVisibility,
	isUploading,
	handleTitleChange,
	showEducationImage,
	checkLimitExceed,
}: any) => {
	const { translate } = useTranslate();
	const { data: countries = [] } = useCountryListQuery();
	const [addressKey, setAddressKey] = useState(0);

	const { resumeData }: any = useResumeStore((state) => state);
	const fieldVisibility = resumeData?.manageTemplateFields?.[slug] || false;

	let validationSchema = Yup.object().shape({
		degree: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.degree') }))
			.max(
				100,
				translate('Validation.max', {
					field: translate('PlaceHolder.degree'),
					number: `100`,
				}),
			),
		instituteName: Yup.string()
			.required(translate('Validation.required', { field: translate('PlaceHolder.instituteName') }))
			.max(
				100,
				translate('Validation.max', {
					field: translate('PlaceHolder.instituteName'),
					number: `100`,
				}),
			),
		countryId: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.country') }),
		),
		stateId: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.state') }),
		),
		cityId: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.city') }),
		),
		currentlyStudying: Yup.boolean().optional(),
		startDate: Yup.date()
			.min(new Date('1900-01-01'), 'Date cannot be before 1900-01-01')
			.max(new Date(), 'Start date cannot be after today')
			.required(translate('Validation.required', { field: translate('PlaceHolder.startDate') })),
		endDate: Yup.date().when('currentlyStudying', {
			is: false,
			then: (schema) =>
				schema
					.required(translate('Validation.required', { field: translate('PlaceHolder.endDate') }))
					.min(Yup.ref('startDate'), 'end date cannot be before start date')
					.max(new Date(), 'Start date cannot be after today'),
			otherwise: (schema) => schema.optional(),
		}),
	});

	if (showEducationImage) {
		validationSchema = validationSchema.shape({
			instituteLogo: Yup.string(),
		});
	}

	const formik = useFormik({
		initialValues: data?.length
			? data[data.length - 1]
			: {
					degree: '',
					instituteName: '',
					instituteLogo: '',
					startDate: '',
					endDate: '',
					countryId: '',
					stateId: '',
					cityId: '',
					currentlyStudying: false,
				},
		enableReinitialize: true,
		validationSchema: validationSchema,
		onSubmit: (values: any) => {
			handleAddMoreClick(slug);
			formik.resetForm();
			formik.setFieldValue('address', '');
			formik.setFieldValue('cityId', '');
			formik.setFieldValue('stateId', '');
			formik.setFieldValue('countryId', '');
			setAddressKey((prev) => prev + 1);
		},
	});

	const renderError = <T extends keyof EducationInfoInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const handleChange = (slug: any, event: any) => {
		onHandleChange(slug, event, data?.length - 1);
		formik.setFieldValue(event.target.name, event.target.value);
	};

	const checkLimits = useMemo(() => {
		return checkLimitExceed(slug);
	}, [data?.length, fieldVisibility]);

	const handleInputChange = (_value: any) => {
		formik.setFieldValue('address', '');
		formik.setFieldValue('cityId', '');
		formik.setFieldValue('stateId', '');
		formik.setFieldValue('countryId', '');
	};
	return (
		<div className="resume-info">
			<Form onSubmit={formik.handleSubmit} autoComplete="off">
				<div className="build-resume-edit-header">
					<ActionMenu
						data={data}
						handleRemoveClick={handleRemoveClick}
						slug="educationTitle"
						contentSlug={slug}
						handleModalVisibility={handleModalVisibility}
						formikObj={formik}
						handleTitleChange={handleTitleChange}
					/>
					<p className="mb-0">{translate('Resume.includeYourAcademicAccolades')}</p>
				</div>
				{fieldVisibility && (
					<div className="build-resume-edit-fields">
						{!checkLimits && (
							<Row key={`${slug}`}>
								<Col xl="6">
									<div className="text-start">
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">
												{translate('PlaceHolder.degreeName')}
											</Form.Label>
											<Form.Control
												id="degree"
												name="degree"
												type="text"
												placeholder={translate('PlaceHolder.degreeName')}
												onChange={(event: any) => {
													onHandleChange(slug, event, data?.length - 1);
													formik.setFieldValue(event.target.name, event.target.value);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.degree}
												maxLength={100}
											/>
											{renderError('degree')}
										</Form.Group>
									</div>
								</Col>
								<Col xl="6">
									<div className="text-start">
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">
												{translate('PlaceHolder.instituteName')}
											</Form.Label>
											<Form.Control
												id="instituteName"
												name="instituteName"
												type="text"
												placeholder={translate('PlaceHolder.instituteName')}
												onChange={(event: any) => {
													onHandleChange(slug, event, data?.length - 1);
													formik.setFieldValue(event.target.name, event.target.value);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.instituteName}
												maxLength={100}
											/>
											{renderError('instituteName')}
										</Form.Group>
									</div>
								</Col>
								<Col xl="6">
									<div className="text-start">
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('PlaceHolder.startDate')}</Form.Label>
											<Form.Control
												id="startDate"
												name="startDate"
												type="date"
												placeholder={translate('PlaceHolder.startDate')}
												onChange={(event: any) => {
													onHandleChange(slug, event, data?.length - 1);
													formik.setFieldValue(event.target.name, event.target.value);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.startDate}
												max={formik.values.endDate}
												min={minDate}
											/>
											{renderError('startDate')}
										</Form.Group>
									</div>
								</Col>
								<Col xl="6">
									<div className="text-start">
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('PlaceHolder.endDate')}</Form.Label>
											<Form.Control
												id="endDate"
												name="endDate"
												type="date"
												placeholder={translate('PlaceHolder.endDate')}
												onChange={(event: any) => {
													onHandleChange(slug, event, data?.length - 1);
													formik.setFieldValue(event.target.name, event.target.value);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.endDate}
												disabled={formik.values.currentlyStudying}
												min={formik.values.startDate}
											/>
											{renderError('endDate')}

											<div className="text-start current-year-btn">
												<Form.Check
													name="currentlyStudying"
													type="switch"
													id="custom-switch"
													label={translate('PlaceHolder.currentlyPursuing')}
													onChange={(event) => {
														onHandleChange(slug, event, data?.length - 1);
														formik.setFieldValue(event.target.name, event.target.checked);
														if (event.target.checked) {
															formik.setFieldValue('endDate', '');
														}
													}}
													checked={formik.values.currentlyStudying}
												/>
											</div>
										</Form.Group>
									</div>
								</Col>
								<Col xl="6">
									<div className="text-start">
										<Form.Group className="mb-4 position-relative custom-search-address">
											<Form.Label className="lh-1">{translate('PlaceHolder.location')}</Form.Label>
											<CustomSearchAddress
												key={addressKey}
												selectProps={{
													placeholder: translate('PlaceHolder.locationText'),
													onChange: (value: any) => {
														const eventObj = {
															target: {
																name: 'address',
																value: value,
															},
														};
														formik.setFieldValue('address', value?.location);
														formik.setFieldValue('cityId', value?.cityId);
														formik.setFieldValue('stateId', value?.stateId);
														formik.setFieldValue('countryId', value?.countryId);
														handleChange(slug, eventObj);
													},
													name: 'address',
												}}
												fieldValue={formik.values.address}
												handleInputChange={handleInputChange}
												onBlur={formik.handleBlur}
												formik={formik}
											/>
											<i className="ri-search-line form-icon position-absolute lh-1"></i>
											{renderError('address') ||
												renderError('cityId') ||
												renderError('stateId') ||
												renderError('countryId')}
										</Form.Group>
									</div>
								</Col>
								{showEducationImage && (
									<Col xl="12">
										<Form.Group className="input-field text-center" controlId="formFile">
											<label className="upload-field d-flex align-items-center justify-content-center w-100">
												<i className="ri-upload-line me-2"></i>
												{isUploading?.[slug]
													? translate('Resume.uploading')
													: data?.[data.length - 1]?.instituteLogo
														? translate('Resume.logoUploaded')
														: translate('Resume.uploadLogo')}
												<Form.Control
													type="file"
													name="instituteLogo"
													onChange={(event) =>
														handleFileChange({ slug, event, index: data?.length - 1 })
													}
												/>
											</label>
										</Form.Group>
										{renderError('instituteLogo')}
									</Col>
								)}
								<Col xl="12" className="mt-3">
									<AddMoreButton title={translate('Resume.addEducation')} />
								</Col>
							</Row>
						)}
						{data?.length > 0 &&
							data?.map((field: any, index: number) => {
								const {
									degree,
									instituteName,
									instituteLogo,
									startDate,
									endDate,
									currentlyStudying,
									cityName,
									stateName,
									countryCode,
								} = field ?? {};
								if (data?.length > 1 && data?.length - 1 !== index) {
									return (
										<InfoCard
											key={index}
											content={{
												title: instituteName,
												subtitle: calculateTimeSpan(startDate, endDate, currentlyStudying, true),
												description: `${degree}, ${[cityName, stateName, countryCode]
													.filter(Boolean)
													.join(', ')}`,
												logo: instituteLogo,
											}}
											slug={slug}
											index={index}
											handleRemove={handleRemoveClick}
											handleModalVisibility={handleModalVisibility}
										/>
									);
								}
							})}
					</div>
				)}
			</Form>
		</div>
	);
};

export default EducationInfo;
